import { Input, Select, Checkbox } from "antd";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";

import AssetUnlockToggler from "@/components/FormEngine/Editor/DragAndDropTool/Field/AssetUnlockToggler";
import ConsequenceComponent from "@/components/FormEngine/Editor/DragAndDropTool/Field/Severity/ConsequenceComponent";
import DefectsComboBox from "@/components/FormEngine/Editor/DragAndDropTool/Field/Severity/DefectsComboBox";
import InspectedComponent from "@/components/FormEngine/Editor/DragAndDropTool/Field/Severity/InspectedComponent";
import MaterialsComboBox from "@/components/FormEngine/Editor/DragAndDropTool/Field/Severity/MaterialsComboBox";
import ProbabilityComponent from "@/components/FormEngine/Editor/DragAndDropTool/Field/Severity/ProbabilityComponent";
import RiskComponent from "@/components/FormEngine/Editor/DragAndDropTool/Field/Severity/RiskComponent";
import SeverityCriteria from "@/components/FormEngine/Editor/DragAndDropTool/Field/Severity/SeverityCriteria";
import { SchemaViewerFormItem } from "@/modules/schema-viewer";

// TODO DEV-8551 `Option` is deprecated. Please use `options` instead.
// eslint-disable-next-line @typescript-eslint/no-deprecated
const { Option } = Select;

/** @deprecated use `FormEngineField` instead -> TODO migrate each field one by one */
const Field = ({
  schema,
  label,
  type,
  model,
  placeholder,
  max,
  rows = 3,
  values = [],
  disabled = false,
  readOnly = false,
  datasetsImage = [],
  isFilled: _isFilled,
  isVisible,
  datasetsImageLoading,
  ...rest
}) => {
  const { t } = useTranslation();

  const fallbackTranslation = (value) => value ?? "";
  const fieldLabel = t(`form:staticField.${model}.label`, label);

  const ContentFragment = (model) => {
    switch (model) {
      case "probability":
        return (
          <ProbabilityComponent
            data-testid={type}
            allowClear
            disabled={disabled}
            readOnly={readOnly}
          />
        );
      case "consequence":
        return (
          <ConsequenceComponent
            data-testid={type}
            allowClear
            disabled={disabled}
            readOnly={readOnly}
          />
        );
      case "risk":
        return (
          <RiskComponent
            data-testid={type}
            disabled={disabled}
            readOnly={readOnly}
          />
        );
    }
    switch (type) {
      case "textArea":
        return (
          <Input.TextArea
            data-testid={type}
            placeholder={placeholder}
            maxLength={max}
            rows={rows}
            showCount={!!max}
            disabled={disabled}
            readOnly={readOnly}
          />
        );
      case "checkbox":
        return (
          <Checkbox data-testid={type} disabled={disabled} readOnly={readOnly}>
            {fieldLabel}
          </Checkbox>
        );
      case "assetUnlockToggler":
        return (
          <AssetUnlockToggler id={model} placeholder={placeholder ?? ""} />
        );
      case "select":
        return (
          <Select
            data-testid={type}
            placeholder={t(
              `form:staticField.${model}.placeholder`,
              fallbackTranslation(placeholder)
            )}
            disabled={disabled}
            readOnly={readOnly}
            allowClear
          >
            {Array.isArray(values) &&
              values.map((value) => (
                <Option key={`select-value-${value}`} value={value}>
                  {t(
                    `form:staticField.${model}.levels.${value}`,
                    fallbackTranslation(value)
                  )}
                </Option>
              ))}
          </Select>
        );
      case "inspectedComponent":
        return (
          <InspectedComponent
            data-testid={type}
            placeholder={t("form:staticField.component.placeholder")}
            disabled={disabled}
            readOnly={readOnly}
            allowClear
          />
        );
      case "materials":
        return (
          <MaterialsComboBox
            data-testid={type}
            placeholder={t("form:staticField.material.placeholder")}
            disabled={disabled}
            readOnly={readOnly}
            allowClear
          />
        );
      case "defects":
        return (
          <DefectsComboBox
            data-testid={type}
            placeholder={t("form:staticField.defect.placeholder")}
            disabled={disabled}
            readOnly={readOnly}
            datasetsImage={datasetsImage}
            datasetsImageLoading={datasetsImageLoading}
            allowClear
          />
        );
      case "severities":
        return (
          <SeverityCriteria
            data-testid={type}
            placeholder={t("form:staticField.severity.placeholder")}
            disabled={disabled}
            readOnly={readOnly}
          />
        );
    }
    return <></>;
  };

  return (
    <SchemaViewerFormItem schema={schema} isVisible={isVisible} {...rest}>
      {ContentFragment(model)}
    </SchemaViewerFormItem>
  );
};

Field.propTypes = {
  schema: PropTypes.object.isRequired,
  label: PropTypes.string.isRequired,
  type: PropTypes.string.isRequired,
  model: PropTypes.string.isRequired,
  inputType: PropTypes.string,
  placeholder: PropTypes.string,
  min: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  max: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  rows: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  values: PropTypes.array,
  disabled: PropTypes.bool,
  readOnly: PropTypes.bool,
  datasetsImage: PropTypes.array,
  datasetsImageLoading: PropTypes.bool,
  isVisible: PropTypes.bool.isRequired,
};

export default Field;
